import React, { useEffect, useState } from 'react'
import { InputField } from '../../molecules/input/input-field'
import { Button } from '../../atoms/button/button'
import VerifyOtpSection from '../verify-otp/verify-otp'
import { EmailCheck, validateEmail } from '../../../utils/helper-functions'
import { t } from '../../../utils/i18n'
import { msalInstance } from '../../../configurations/msal-config'

import './signin-form.scss'
import { useStorage } from '../../../customHooks/useStorage'
import axios from '../../../utils/axios'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../customHooks/useAuth'

export type SessionToken = {
  token: string
  email: string
}

interface iSignInForm {
  linkAccountView?: boolean
  customCss?: string
  setPageRefresh?: (istrue: boolean) => void
}

const SignInForm: React.FC<iSignInForm> = ({ linkAccountView, customCss, setPageRefresh }) => {
  const { value: otpBlockTime } = useStorage('otp_block_until', null, 'local')
  const { setValue: setSessionToken } = useStorage<SessionToken>(
    'token',
    { token: '', email: '' },
    'session'
  )
  const [sendOTPDisabled, SetSendOTPDisabled] = useState(true)
  const [showOTPScreen, setShowOTPScreen] = useState(false)
  const [isEmailValid, setEmailValid] = useState(false)
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isUserBlocked = Boolean(otpBlockTime)

  const navigate = useNavigate()
  const { user } = useAuth()

  useEffect(() => {
    setTimeout(() => {
      const input = document.getElementById('email') as HTMLInputElement
      if (input?.value) {
        setEmail(input.value)
        const isValid = validateEmail(input.value)
        setEmailValid(isValid.ok)
        SetSendOTPDisabled(!isValid.ok) // disables if invalid
        checkEmail(isValid)
      }
    }, 300)
  }, [])

  const handleEmailInput = (value: string) => {
    //VALIDATE EMAIL WITH REGEX
    setEmail(value)
    const isValid = validateEmail(value)
    setEmailValid(isValid.ok)
    SetSendOTPDisabled(!isValid.ok) // disables if invalid
    checkEmail(isValid)
  }

  const handleEmailLogin = async () => {
    if (!isEmailValid) return
    try {
      if (!linkAccountView) {
        const config = {
          url: process.env.REACT_APP_SEND_OTP,
          method: 'POST',
          data: { email: email },
        }
        const signInResponse = await axios(config)

        if (signInResponse.data.data.code === 'USER_AUTHORIZATION_FAILED') {
          setError(t('USER_AUTHORIZATION_FAILED'))
          setShowOTPScreen(false)
          return
        }
        //verify otp
        if (!signInResponse.data.data.result.Session) return
        setSessionToken({ token: signInResponse.data.data.result.Session, email: email })
        setShowOTPScreen(true)
      } else {
        const config = {
          url: process.env.REACT_APP_LINK_ACCOUNT,
          method: 'POST',
          data: { accountToLink: email },
          headers: {
            'x-user': user?.email,
            'x-access-token': user?.tokens?.accessToken?.token,
            'x-source': user?.isSSO ? 'sso' : null,
          },
        }
        const linkAccResponse = await axios(config)
        if (linkAccResponse.data.data.status !== 'ok') return
        setSessionToken({ token: '', email: email })
        setShowOTPScreen(true)
      }
      // eslint-disable-next-line
    } catch (error: any) {
      const errorCode = error?.response?.data?.data?.code
      setError(t(errorCode))
      setShowOTPScreen(false)
    }
  }

  const checkEmail = (res: EmailCheck) => {
    if (res.ok) setError(null)
    else if (res.reason === 'bad-format') setError(t('email.invalid'))
    else if (res.reason === 'groupe-domain') setError(t('groupe.domain.notValid'))
    else if (res.reason === 'email-empty') setError(t('email.required'))
  }

  const handleChangeEmail = (value: boolean) => {
    setShowOTPScreen(value)
  }

  const handleSSOLogin = async () => {
    await msalInstance.initialize()
    msalInstance.loginRedirect({
      scopes: ['user.read', 'Offline_access'],
      prompt: 'select_account',
    })
  }

  return (
    <div className={`signin-form-wrapper body-sm ${customCss ? customCss : ''}`}>
      {/*
            NEED CUSTOM ANCHOR ELE AND REPLACE THE LOGO SECTION
            */}
      {!linkAccountView && (
        <div className="logo-container">
          <img src="/logo/logo.svg" alt={t('brand.logo')} className="logo" />
        </div>
      )}

      <div className="form-card">
        {!showOTPScreen && !isUserBlocked ? (
          <>
            {!linkAccountView && (
              <>
                <h1 className="form-title">{t('signin.header')}</h1>
                <p className="sso-text">{t('have.llid')}</p>
                <Button
                  className="sso-button"
                  label={t('sso.signin')}
                  handleClick={handleSSOLogin}
                  disabled={false}
                />
                <hr className="divider" />
                <p className="or-text">{t('login.with.email')}</p>
              </>
            )}
            <InputField
              label={t('email')}
              placeholder={t('email.placeholder')}
              fieldType="email"
              required
              value={email}
              onChange={(e) => handleEmailInput(e)}
              error={error}
              tooltipContent={t('email.tooltip')}
              autoComplete={false}
            />
            <div className="signin-buttons">
              <Button
                variant={`${linkAccountView ? 'secondary' : 'primary'}`}
                className="send-otp-button"
                disabled={sendOTPDisabled}
                label={linkAccountView ? t('send.otp.verify') : t('send.otp')}
                handleClick={handleEmailLogin}
              />
              {linkAccountView && (
                <Button
                  className="send-otp-button skip-link"
                  label={t('skip.now')}
                  handleClick={() => navigate('/overview', { replace: true })}
                />
              )}
            </div>
          </>
        ) : (
          <VerifyOtpSection
            error={t('invalid.otp')}
            handleChangeEmail={handleChangeEmail}
            handleEmailLogin={handleEmailLogin}
            linkAccountView={linkAccountView}
            setShowOTPScreen={setShowOTPScreen}
            setPageRefresh={setPageRefresh}
            setEmail={setEmail}
          />
        )}
      </div>
      <div className="privacy-policy">
        <a
          href="https://www.publicisresources.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('privacy.policy')}
        </a>
      </div>
    </div>
  )
}

export default SignInForm
